import {get} from "lodash"
import BaseService from "./Base"
import {
    AdvisorCallStatsItem,
    AdvisorCallStatsRequest,
    AdvisorSpeedToLeadStatsItem,
    AdvisorSpeedToLeadStatsRequest
} from "types/admissions"

class AdmissionService extends BaseService {
    async getAllLeadSources(params = {}) {
        const response = await this.post("/v1/admissions/lead-sources/list", params)
        return get(response, "data", [])
    }

    async getMaxIdLeadSources(params = {}) {
        const response = await this.post("/v1/admissions/lead-sources/get-max-id", params)
        return get(response, "data.data", 0)
    }

    async createLeadSource(params = {}) {
        const response = await this.post("/v1/admissions/lead-sources/create", params)
        return get(response, "data.data", {})
    }

    async duplicateLeadSource(leadSources) {
        const response = await this.post(`/v1/admissions/lead-sources/create`, leadSources)
        return get(response, "data", {})
    }

    async updateLeadSource(id, data, params = {}) {
        data.admissionsLeadSourceId = id
        const response = await this.post(`/v1/admissions/lead-sources/edit`, data, params)
        return get(response, "data", {})
    }

    async deleteLeadSource(admissionsLeadSourceIds) {
        const response = await this.post(`/v1/admissions/lead-sources/delete`, {admissionsLeadSourceIds})
        return get(response, "data", {})
    }

    async getAllLeadDistributions(params = {}) {
        const response = await this.post("/v1/admissions/lead-distributions/list", params)
        return get(response, "data", [])
    }

    async saveLeadDistributions(params = {}) {
        const response = await this.post("/v1/admissions/lead-distributions/save", params)
        return get(response, "data.data", {})
    }

    async deleteLeadDistributions(params = {}) {
        const response = await this.post("/v1/admissions/lead-distributions/delete", params)
        return get(response, "data.data", {})
    }

    async getLeadDistributionSettingsList(params = {}) {
        const response = await this.post("/v1/admissions/lead-distributions/settings/list", params)
        return get(response, "data", [])
    }

    async saveLeadDistributionSettings(params = {}) {
        const response = await this.post("/v1/admissions/lead-distributions/settings/save", params)
        return get(response, "data", [])
    }

    async getLeadList(params = {}) {
        const response = await this.post("/v1/leads/list", params)
        return get(response, "data", [])
    }

    async createNewLead(params = {}) {
        const response = await this.post("/v1/leads/manual-create", params)
        return get(response, "data.data", {})
    }

    async deleteLead(params = {}) {
        const response = await this.post("/v1/leads/delete", params)
        return get(response, "data", {})
    }

    async updateStatusLead(params = {}) {
        const response = await this.post("/v1/leads/update-processing-status", params)
        return get(response, "data", {})
    }

    async getMaxIdTargets(params = {}) {
        const response = await this.post("/v1/admissions/targets/get-max-id", params)
        return get(response, "data.data", 0)
    }

    async getAllTargets(params = {}) {
        const response = await this.post("/v1/admissions/targets/list", params)
        return get(response, "data", [])
    }

    async createTarget(params = {}) {
        const response = await this.post("/v1/admissions/targets/create", params)
        return get(response, "data.data", {})
    }

    async getOneTarget(params = {}) {
        const response = await this.post(`/v1/admissions/targets/get`, params)
        return get(response, "data.data", {})
    }

    async updateTarget(params = {}) {
        const response = await this.post(`/v1/admissions/targets/edit`, params)
        return get(response, "data.data", {})
    }

    async deleteTargets(params = {}) {
        const response = await this.post("/v1/admissions/targets/delete", params)
        return get(response, "data.data", {})
    }

    async getAllHowDidYouKnowAbouUs(params = {}) {
        const response = await this.post("/v1/admissions/how-did-you-know-about-us/list", params)
        return get(response, "data", [])
    }

    async createHowDidYouKnowAbouUs(params = {}) {
        const response = await this.post("/v1/admissions/how-did-you-know-about-us/create", params)
        return get(response, "data.data", {})
    }

    async updateHowDidYouKnowAbouUs(id, data, params = {}) {
        data.howDidYouKnowAboutUsId = id
        const response = await this.post(`/v1/admissions/how-did-you-know-about-us/edit`, data, params)
        return get(response, "data", {})
    }

    async deleteHowDidYouKnowAbouUs(howDidYouKnowAboutUsIds) {
        const response = await this.post(`/v1/admissions/how-did-you-know-about-us/delete`, {howDidYouKnowAboutUsIds})
        return get(response, "data", {})
    }

    async getAllFunnelStatuses(params = {}) {
        const response = await this.post("/v1/admissions/funnel-statuses/list", params)
        return get(response, "data", [])
    }

    async createFunnelStatus(params = {}) {
        const response = await this.post("/v1/admissions/funnel-statuses/create", params)
        return get(response, "data.data", {})
    }

    async updateFunnelStatus(params = {}) {
        const response = await this.post(`/v1/admissions/funnel-statuses/edit`, params)
        return get(response, "data", {})
    }

    async deleteFunnelStatus(params = {}) {
        const response = await this.post("/v1/admissions/funnel-statuses/delete", params)
        return get(response, "data.data", {})
    }

    async getFunnelStats(params = {}) {
        const response = await this.post("/v1/admissions/funnel-stats/list", params)
        return get(response, "data.data", [])
    }

    async getStageFunnelStats(params = {}) {
        const response = await this.post("/v1/admissions/stage-funnel-stats/list", params)
        return get(response, "data.data", [])
    }

    async getEnrollmentStats(params = {}) {
        const response = await this.post("/v1/admissions/enrollment-stats", params)
        return get(response, "data.data", [])
    }

    async getStudentStatusProgress(studentId: number) {
        const response = await this.post("/v1/admissions/funnel-statuses/student-progress", {studentId})
        return get(response, "data.data", [])
    }

    async getConverionRateStatus() {
        const response = await this.post("/v1/admissions/funnel-statuses/rates/get", {})
        return get(response, "data.data", {})
    }

    async updateConverionRateStatus(params = {}) {
        const response = await this.post("/v1/admissions/funnel-statuses/rates/update", params)
        return get(response, "data.data", {})
    }

    async listLeadSourceStats(params = {}) {
        const response = await this.post("/v1/admissions/lead-source-stats/list", params)
        return get(response, "data", [])
    }

    async listLeadSourceByProfileStats(params = {}) {
        const response = await this.post("/v1/admissions/lead-source-stats/by-profile-state", params)
        return get(response, "data", [])
    }

    async listLeadAdvisorByProfileStats(params = {}) {
        const response = await this.post("/v1/admissions/advisor-stats/by-profile-state", params)
        return get(response, "data", [])
    }

    async listAdvisorStats(params = {}) {
        const response = await this.post("/v1/admissions/advisor-stats/list", params)
        return get(response, "data", [])
    }

    async listAdvisorActivityStats(params = {}) {
        const response = await this.post("/v1/admissions/advisor-activity-stats/list", params)
        return get(response, "data", [])
    }

    async listAnalyticsAdvisors(params = {}) {
        const response = await this.post("/v1/admissions/analytics/advisors/list", params)
        return get(response, "data", [])
    }

    async getAllConversionStatuses(params = {}) {
        const response = await this.post("/v1/admissions/conversion-statuses/list", params)
        return get(response, "data", [])
    }

    async createConversionStatus(params = {}) {
        const response = await this.post("/v1/admissions/conversion-statuses/create", params)
        return get(response, "data.data", {})
    }

    async updateConversionStatus(params = {}) {
        const response = await this.post(`/v1/admissions/conversion-statuses/edit`, params)
        return get(response, "data", {})
    }

    async deleteConversionStatus(params = {}) {
        const response = await this.post("/v1/admissions/conversion-statuses/delete", params)
        return get(response, "data.data", {})
    }

    async getConversionDetailsReport(params = {}) {
        const response = await this.post("/v1/admissions/conversion-details-report", params)
        return get(response, "data", [])
    }

    async getConversionSummaryReport(params = {}) {
        const response = await this.post("/v1/admissions/conversion-summary-report", params)
        return get(response, "data", [])
    }

    async getAnalyticsByProgramReport(params = {}) {
        const response = await this.post("/v1/admissions/analytics-by-program-report", params)
        return get(response, "data", [])
    }

    async getAdvisorCallStats(params: AdvisorCallStatsRequest): Promise<AdvisorCallStatsItem[]> {
        const response = await this.post("/v1/admissions/analytics/advisor-call-stats/list", params)
        return get(response, "data.data", [])
    }

    async getAdvisorSpeedToLeadStats(params: AdvisorSpeedToLeadStatsRequest): Promise<AdvisorSpeedToLeadStatsItem[]> {
        const response = await this.post("/v1/admissions/analytics/advisor-speed-to-lead/list", params)
        return get(response, "data.data", [])
    }

    async listCampaignStats(params = {}) {
        const response = await this.post("/v1/admissions/campaign-stats/list", params)
        return get(response, "data", [])
    }
}

export default AdmissionService
